import { useCallback } from "react";
import { Collapse, Typography } from "@mui/material";
import DoctorCard from "../../../components/dashboard/doctorCard/DoctorCard";
import InsuranceCard from "../../../components/dashboard/InsuranceCard/InsuranceCard";
import { getPercentageByTotal } from "../../../utilis/common";
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from "../../../types/community-transfer.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import DayCardList from "../../hospital/components/dayCardList/DayCardList";
import FloorCardList from "../../hospital/components/floorCardList/FloorCardList";
import NinetyDaysAnalysis from "../../hospital/components/ninetyDaysAnalysis/NinetyDaysAnalysis";
import ReturnCardList from "../../hospital/components/returnCardList/ReturnCardList";
import DynamicDataCardList from "../../hospital/components/DynamicDataCardList/DynamicDataCardList";
import { HospitalList } from "../../admissions/components";
import {
	findHospitalDynamicCardByKey,
	HeaderFilterCardShell,
	PanelWrap,
} from "../../hospital/components/HospitalHeaderCardFilterPanel";

/**
 * `PanelWrap` body when rendering {@link HospitalList} with `isMobile` here (compact rows).
 * Reinforces no horizontal scrollbar in the Community Transfer header popover; the underlying
 * fix is in `HospitalList` / `HospitalList.module.scss` and `HeaderFilterCardShell` in
 * `HospitalHeaderCardFilterPanel.js`.
 */
const COMMUNITY_HEADER_HOSPITAL_LIST_PANEL_SX = {
	overflowX: "hidden",
	minWidth: 0,
	px: { xs: 0.5, sm: 0.75 },
};

function CommunityTransferHeaderDynamicFilterBody({
	ele,
	accessor,
	cardFilter,
	filterListData,
	filterListDataComparison,
	handleToggle,
	handleToggleAll,
	filter,
	filterComparison,
	loadingBoth,
	dbData,
	dbDataComparison,
	percentageAgainst,
	averageCensusComparison,
	projectionDays,
	getPriorityNumber,
	transferType,
	lockedTotalBy,
	filterTotal,
}) {
	const cardSelectedItems = cardFilter?.[accessor] ?? [];
	const cardID = filterListData?.[accessor]?.[0]?.id;
	const isSelectedTotalCard = cardSelectedItems.indexOf(cardID) !== -1;
	const isCustomTab = Boolean(ele?.isCustomTab);
	const question = ele?.question;

	const handleCardClick = useCallback(
		async (_titleParam = null, uniqueKey = null) => {
			const compositeKey = uniqueKey && accessor ? `${accessor}__${uniqueKey}` : null;
			const comparisonItem = filterListDataComparison?.[accessor]?.[0];
			await handleToggle({
				item: filterListData?.[accessor]?.[0],
				itemComparison: comparisonItem,
				type: accessor,
				isChecked: isSelectedTotalCard,
				cardTitle: ele.title || ele.label || ele.tableLabel,
				question: {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab: false,
					cardTitle: ele.title || ele.label || ele.tableLabel,
					customTab: ele.type,
					accessor,
					compositeKey,
					uniqueKey,
				},
				isCustomTab: false,
			});
		},
		[accessor, ele, filterListData, filterListDataComparison, handleToggle, isSelectedTotalCard, question]
	);

	return (
		<DynamicDataCardList
			compactForHeaderPopover
			disableInteractions={false}
			totalTransfers={dbData.total}
			totalTransfersComparison={dbDataComparison?.total}
			filterListData={filterListData}
			filterListDataComparison={filterListDataComparison}
			question={ele}
			cardTitle={ele.title || ele.label || ele.tableLabel}
			data={filterListData?.[accessor] ?? []}
			dataComparison={filterListDataComparison?.[accessor] ?? []}
			handleToggle={handleToggle}
			handleToggleAll={handleToggleAll}
			selectedItem={cardFilter?.[accessor]}
			spacialSelectedItem={cardFilter?.[`${accessor}_spacial`]}
			type={accessor}
			filter={filter}
			loading={loadingBoth}
			filterComparison={filterComparison}
			page={PAGE_TYPE.COMMUNITY_TRANSFER}
			averageCensus={percentageAgainst}
			projectionDays={projectionDays}
			isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
			priorityNumber={getPriorityNumber(accessor)}
			transferType={transferType}
			lockedTotalBy={lockedTotalBy}
			isCustomTab={isCustomTab}
			filterTotal={filterTotal}
			handleCardClick={handleCardClick}
			isSelectedTotalCard={isSelectedTotalCard}
			cardFilter={cardFilter}
			isDisabled={false}
			admissionCompareAgainst={null}
			admissionCompareAgainstComparison={null}
			reverseColorsAdmissionPage={false}
			averageCensusComparison={averageCensusComparison}
		/>
	);
}

/**
 * Interactive filter popovers for Community Transfer header chips (same handlers/data as main cards).
 */
export default function CommunityTransferHeaderCardFilterPanel({
	dataKey,
	handleToggle,
	handleToggleAll,
	filterListData,
	filterListDataComparison,
	cardFilter,
	filter,
	filterComparison,
	loading,
	loadingComparison,
	projectionDays,
	filterTotal,
	filterTotalComparison,
	dbData,
	dbDataComparison,
	specialComparisonSet,
	transferType,
	lockedTotalBy,
	getPriorityNumber,
	comparingAgainstDifferentFacility,
	dynamicCards = [],
	communityTransferDynamicTabDataByAccessor = {},
	percentageAgainst,
	headerFilterPopoverOpen,
}) {
	const loadingBoth = loading || loadingComparison;

	const panelContent = (() => {
		switch (dataKey) {
		case CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA:
			return (
				<HeaderFilterCardShell
					title={COMMUNITY_CARD_LABELS.insuranceData}
					bodySx={{ p: 0, pt: 0.5, overflow: "visible" }}
				>
					<InsuranceCard
						cardTitle={COMMUNITY_CARD_LABELS.insuranceData}
						data={filterListData.insuranceData}
						dataComparison={filterListDataComparison?.insuranceData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						selectedItem={cardFilter.insuranceData}
						type="insuranceData"
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						averageCensus={specialComparisonSet ? filterTotal : percentageAgainst}
						averageCensusComparison={
							specialComparisonSet ? filterTotalComparison : dbDataComparison?.censusAverage
						}
						headerFilterPopover
						hideProgressBar
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
					/>
				</HeaderFilterCardShell>
			);

		case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.sixtyDaysData}>
					<NinetyDaysAnalysis
						cardTitle="90 Days Analysis"
						handleToggle={handleToggle}
						loading={loadingBoth}
						selectedItem={cardFilter.sixtyDaysData}
						type="sixtyDaysData"
						data={filterListData.sixtyDaysData}
						comparisonData={filterListDataComparison?.sixtyDaysData}
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						filterListData={filterListData}
						filterListDataComparison={filterListDataComparison}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={specialComparisonSet ? filterTotal : percentageAgainst}
						averageCensusComparison={
							specialComparisonSet ? filterTotalComparison : dbDataComparison?.censusAverage
						}
						headerFilterPopover
						hideRadialSection
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.RETURNS_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.returnsData}>
					<ReturnCardList
						cardTitle="Returned / Didn't Return"
						handleToggle={handleToggle}
						selectedItem={cardFilter.returnsData}
						transfers={dbData.total}
						transfersComparison={dbDataComparison?.total}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type="returnsData"
						data={filterListData.returnsData}
						comparisonData={filterListDataComparison?.returnsData}
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						averageCensus={percentageAgainst}
						averageCensusComparison={dbDataComparison?.censusAverage}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.RETURNS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						compactForHeaderPopover
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.doctorData}>
					<DoctorCard
						cardTitle={COMMUNITY_CARD_LABELS.doctorData}
						data={filterListData?.doctorData ?? []}
						comparisonData={filterListDataComparison?.doctorData}
						minimizable={true}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter?.doctorData ?? []}
						type="doctorData"
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						loading={loadingBoth}
						averageCensus={specialComparisonSet ? filterTotal : percentageAgainst}
						averageCensusComparison={
							specialComparisonSet ? filterTotalComparison : dbDataComparison?.censusAverage
						}
						filter={filter}
						filterComparison={filterComparison}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						forHeaderPopover
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.FLOORS_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.floorsData}>
					<FloorCardList
						cardTitle={COMMUNITY_CARD_LABELS.floorsData}
						data={filterListData.floorsData}
						dataComparison={filterListDataComparison?.floorsData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						loading={loadingBoth}
						selectedItem={cardFilter.floorsData}
						type="floorsData"
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={specialComparisonSet ? filterTotal : percentageAgainst}
						averageCensusComparison={
							specialComparisonSet ? filterTotalComparison : dbDataComparison?.censusAverage
						}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.FLOORS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.DAYS_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.daysData}>
					<DayCardList
						cardTitle={COMMUNITY_CARD_LABELS.daysData}
						data={filterListData.daysData}
						dataComparison={filterListDataComparison?.daysData}
						handleToggle={handleToggle}
						selectedItem={cardFilter.daysData}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type={CO_TRANSFER_CARDS_TYPE.DAYS_DATA}
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						averageCensus={specialComparisonSet ? filterTotal : getPercentageByTotal(dbData)}
						averageCensusComparison={
							specialComparisonSet ? filterTotalComparison : getPercentageByTotal(dbDataComparison)
						}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA:
			return (
				<PanelWrap title="Safe discharges per Assisted Living" contentSx={COMMUNITY_HEADER_HOSPITAL_LIST_PANEL_SX}>
					<HospitalList
						dbData={dbData}
						dbDataComparison={dbDataComparison}
						cardTitle="Safe discharges per Assisted Living"
						total={dbData.total}
						totalComparison={dbDataComparison?.total}
						data={filterListData.safeDischargeAssLivData}
						dataComparison={filterListDataComparison?.safeDischargeAssLivData}
						filter={filter}
						filterComparison={filterComparison}
						handleToggle={handleToggle}
						selectedItem={cardFilter.safeDischargeAssLivData}
						type={CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA}
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						loading={loadingBoth}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						averageCensus={percentageAgainst}
						priorityData={cardFilter.mainPriorityData}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						disableInteractions={false}
						isMobile
					/>
				</PanelWrap>
			);

		case CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA:
			return (
				<PanelWrap title={COMMUNITY_CARD_LABELS.snfFacilityData} contentSx={COMMUNITY_HEADER_HOSPITAL_LIST_PANEL_SX}>
					<HospitalList
						dbData={dbData}
						dbDataComparison={dbDataComparison}
						cardTitle="SNF Transfers per Facility"
						total={dbData.total}
						totalComparison={dbDataComparison?.total}
						data={filterListData.snfFacilityData}
						dataComparison={filterListDataComparison?.snfFacilityData}
						filter={filter}
						filterComparison={filterComparison}
						handleToggle={handleToggle}
						selectedItem={cardFilter.snfFacilityData}
						type={CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA}
						page={PAGE_TYPE.COMMUNITY_TRANSFER}
						loading={loadingBoth}
						comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						averageCensus={percentageAgainst}
						priorityData={cardFilter.mainPriorityData}
						priorityNumber={getPriorityNumber(CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						disableInteractions={false}
						isMobile
					/>
				</PanelWrap>
			);

		default: {
			const dynamicEle =
				communityTransferDynamicTabDataByAccessor[dataKey] ||
				findHospitalDynamicCardByKey(dataKey, dynamicCards);
			if (dynamicEle) {
				const accessor = dynamicEle.accessor || dataKey;
				const dynamicTitle =
					dynamicEle.title || dynamicEle.label || dynamicEle.tableLabel || String(dataKey);
				return (
					<PanelWrap title={dynamicTitle}>
						<CommunityTransferHeaderDynamicFilterBody
							ele={dynamicEle}
							accessor={accessor}
							cardFilter={cardFilter}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							filter={filter}
							filterComparison={filterComparison}
							loadingBoth={loadingBoth}
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							percentageAgainst={percentageAgainst}
							averageCensusComparison={
								specialComparisonSet ? filterTotalComparison : getPercentageByTotal(dbDataComparison)
							}
							projectionDays={projectionDays}
							getPriorityNumber={getPriorityNumber}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							filterTotal={filterTotal}
						/>
					</PanelWrap>
				);
			}
			return (
				<PanelWrap title="Filter">
					<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
						This filter is not available in the header preview.
					</Typography>
				</PanelWrap>
			);
		}
	}
	})();

	if (headerFilterPopoverOpen === undefined) {
		return panelContent;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{panelContent}
		</Collapse>
	);
}
